import Box from '@mui/material/Box';
import type { ReactElement } from 'react';

import { useLanguage } from '../app/i18n';
import { NUMERIC_FONT } from '../app/theme';

import { formatMoney, signOf } from './format-money';

/** Props of {@link Amount}: a contract `Money`, spread, plus display options. */
export interface AmountProps {
  readonly amountMinor: number;
  readonly currency: string;
  /** Result (P&L): writes the sign and colours it green or red. Balances leave it off. */
  readonly signed?: boolean | undefined;
  /** Font size in px; inherits by default (28 for the equity of mockup 1). */
  readonly fontSize?: number | undefined;
  /** Secondary text colour, for an amount that supports a main figure. Ignored when signed. */
  readonly muted?: boolean | undefined;
}

const SIGN_COLOR = {
  positive: 'success.main',
  negative: 'error.main',
  zero: 'inherit',
} as const;

/**
 * Money in the account currency, formatted in the UI language, in IBM Plex Mono with tabular
 * digits (mockups `.num`). Colour is never the only cue: the sign is written (C3 R7).
 *
 * @example <Amount {...position.profit} signed />
 */
export function Amount({
  amountMinor,
  currency,
  signed,
  fontSize,
  muted,
}: AmountProps): ReactElement {
  const language = useLanguage();
  const sign = signOf(amountMinor);
  return (
    <Box
      component="span"
      data-sign={signed === true ? sign : undefined}
      sx={{
        fontFamily: NUMERIC_FONT,
        fontVariantNumeric: 'tabular-nums',
        fontWeight: 500,
        whiteSpace: 'nowrap',
        fontSize,
        color: signed === true ? SIGN_COLOR[sign] : muted === true ? 'text.secondary' : 'inherit',
      }}
    >
      {formatMoney({
        amountMinor,
        currency,
        language,
        signed,
      })}
    </Box>
  );
}
